import React, { useEffect, useState } from "react";
import {
  Table, Modal, Tooltip, Spin,
} from "antd";
import { EyeInvisibleOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import { fetchSamplesFromCoc } from "../../../actions/dashboard";
import { getTestStatusClassName, getUser } from "../../../utils/helpers";
import "./SamplesPreview.css";

const SamplesPreview = ({ modalData, closeModal }) => {
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const columnWidth = "200px";
  const user = getUser();

  const columns = [
    {
      title: "Genista ID",
      dataIndex: "genista_id",
      key: "genista_id",
      width: columnWidth,
      hidden: modalData.type === "coc",
    },
    {
      title: "Sample ID",
      dataIndex: "sample_id",
      key: "sample_id",
      width: columnWidth,
    },
    {
      title: "Sample Type",
      dataIndex: "sample_type",
      key: "sample_type",
      width: columnWidth,
    },
    {
      title: "Test",
      dataIndex: "tests",
      key: "tests",
      width: columnWidth,
    },
  ];

  const SampleTableCell = ({
    children, dataIndex, record, type, ...restProps
  }) => {
    let childNode = children;
    if (dataIndex === "tests") {
      const isWo = type === "wo";
      childNode = (
        <div className="TestCardContainer">
          {record.tests.map((test) => {
            const {
              test_internal_name, internal_name, due_by, due_flag, test_status, result,
            } = test;
            // display the due by date if this is a wo modal and the due_flag is 1
            const tooltipTitle = isWo && due_flag === "1" ? due_by : "";
            return (
              <Tooltip
                key={`${record.key}-${test_internal_name ?? internal_name}`}
                placement="topLeft"
                title={tooltipTitle}
              >
                <div className="TestCard">
                  {isWo && due_flag === "1" && (
                  <>
                    <i className="fa fa-clock-o batchWOOverDue" />
                    {" "}
                  </>
                  )}
                  <span>
                    {isWo && (<strong>{`${test_internal_name ?? internal_name}${isWo ? ": " : ""}`}</strong>)}
                    {!isWo && <span>{test_internal_name ?? internal_name}</span>}
                  </span>
                  {isWo && (
                  <span>
                    {`${result ?? "-"}, `}
                  </span>
                  )}
                  {isWo && <span className={getTestStatusClassName(test_status)}>{test_status}</span>}
                </div>
              </Tooltip>
            );
          })}
        </div>
      );
    }

    if ((dataIndex === "sample_id" && modalData.type === "coc") || dataIndex === "genista_id") {
      childNode = (
        <div className="SamplePreview__SampleIdContainer">
          {record.sample_hidden === 1 && user && user.permissionMap.view_hidden_sample_icon_automation && <EyeInvisibleOutlined />}
          {children}
        </div>
      );
    }

    return <td {...restProps}>{childNode}</td>; // eslint-disable-line
  };

  /**
   * Return a list of object for the table data
   */
  const setSampleData = (sample_data) => {
    const sampleList = sample_data.map((sample) => {
      const {
        tests, id, coc_sample_id, sample_type, genista_id, sample_id, sample_hidden,
      } = sample;
      return {
        key: id ?? genista_id,
        tests,
        sample_id: coc_sample_id ?? sample_id,
        genista_id: genista_id ?? "",
        sample_type,
        sample_hidden,
      };
    });
    if (user.permissionMap.view_hidden_samples) {
      return sampleList;
    }

    return sampleList.filter((sample) => sample.sample_hidden !== 1);
  };

  /**
   * Fetch samples information based on a COC ID
   */
  const fetchSamplesApi = async () => {
    setLoading(true);
    const { id, wo_generated } = modalData;
    const {
      success, message, result_list, canceled,
    } = await fetchSamplesFromCoc({
      coc_id: id,
      wo_status: wo_generated !== false,
    });

    if (success) {
      setTableData(
        result_list.map((item) => ({
          po: item.po || "-",
          workorder_id: item?.sample_data[0]?.workorder_id || "-",
          sample_data: setSampleData(item.sample_data),
          workorder_is_deleted: item.workorder_is_deleted,
        })),
      );
    } else if (!canceled) {
      setTableData([]);
      toast.error(message);
    }
    setLoading(canceled === undefined ? false : canceled);
  };

  /**
   * Set the table data based on WO data
   */
  const setTableDataFromWo = () => {
    setLoading(true);
    const { workorder_id, sample_list, workorder_is_deleted } = modalData;
    setTableData([
      {
        po: "-",
        sample_data: setSampleData(sample_list),
        workorder_id,
        workorder_is_deleted,
      },
    ]);
    setLoading(false);
  };

  useEffect(() => {
    if (modalData.type === "coc") {
      fetchSamplesApi();
    } else {
      setTableDataFromWo();
    }
  }, [modalData]); // eslint-disable-line 

  const modalTitle = (table) => (
    <div className="SamplePreview__TableHeader">
      {table.workorder_id && (
        <div>
          <strong>WO:</strong>
          &nbsp;
          {user.permissionMap.view_hidden_workorder_icon_automation && table.workorder_is_deleted === 2 && (
          <>
            <EyeInvisibleOutlined />
            &nbsp;
          </>
          )}
          <span>{table.workorder_id}</span>
        </div>
      )}
      {modalData.type === "coc" && (
        <div>
          <strong>PO:</strong>
          &nbsp;
          <span>{table.po}</span>
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open
      centered
      destroyOnClose
      onCancel={closeModal}
      footer={null}
      title="Sample Detail"
      width="80vw"
    >
      <Spin spinning={loading} size="large">
        <div className="SamplesPreviewContainer">
          {tableData.map((table, index) => (
            <Table
              key={index}
              className="SamplesPreview__Table"
              title={() => modalTitle(table)}
              pagination={false}
              loading={loading}
              dataSource={table.sample_data}
              components={{
                body: {
                  cell: SampleTableCell,
                },
              }}
              columns={columns.map((col) => ({
                ...col,
                onCell: (record) => ({
                  record,
                  type: modalData.type,
                  dataIndex: col.dataIndex,
                }),
              }))}
            />
          ))}
        </div>
      </Spin>
    </Modal>
  );
};

export default SamplesPreview;
